import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { today, yearStart } from "@/lib/format";
import { DateRange, Money, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CtPayments } from "@/components/CtPayments";
import { FxReport } from "@/components/FxReport";
import { Cashflow } from "@/components/Cashflow";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Section({ title, rows, total, totalLabel }) {
  const { accName } = useLang();
  return (
    <div className="mb-6">
      <h4 className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{title}</h4>
      <table className="data-table compact">
        <tbody>
          {rows.filter((r) => r.balance).map((r) => (
            <tr key={r.id}><td><span className="me-2 font-mono text-slate-400">{r.code}</span>{accName(r)}</td><td className="text-end"><Money value={r.balance} cur={false} /></td></tr>
          ))}
          {totalLabel && <tr className="border-t-2 border-slate-900 font-bold"><td>{totalLabel}</td><td className="text-end"><Money value={total} /></td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TrialBalance({ range }) {
  const { t, accName } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/trial-balance", { params: range }).then((r) => setD(r.data)); }, [range]);
  if (!d) return null;
  return (
    <table className="data-table" data-testid="trial-balance-table">
      <thead><tr><th>{t("code")}</th><th>{t("account")}</th><th className="text-end">{t("debit")}</th><th className="text-end">{t("credit")}</th></tr></thead>
      <tbody>
        {d.rows.map((r) => (
          <tr key={r.id}><td className="font-mono">{r.code}</td><td>{accName(r)}</td><td className="text-end">{r.debit_balance ? <Money value={r.debit_balance} cur={false} /> : ""}</td><td className="text-end">{r.credit_balance ? <Money value={r.credit_balance} cur={false} /> : ""}</td></tr>
        ))}
        <tr className="bg-[#0B1220] font-bold text-white"><td colSpan={2}>{t("total")}</td><td className="text-end"><Money testid="tb-total-debit" value={d.total_debit} /></td><td className="text-end"><Money testid="tb-total-credit" value={d.total_credit} /></td></tr>
      </tbody>
    </table>
  );
}

function ProfitLoss({ range }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/profit-loss", { params: range }).then((r) => setD(r.data)); }, [range]);
  if (!d) return null;
  return (
    <div className="max-w-2xl p-6" data-testid="profit-loss-report">
      <Section title={t("revenue")} rows={d.revenue} total={d.total_revenue} totalLabel={t("totalRevenue")} />
      <Section title={t("expensesLabel")} rows={d.expenses} total={d.total_expenses} totalLabel={t("totalExpenses")} />
      <div className={`flex justify-between rounded-lg p-4 text-lg font-bold ${d.net_profit >= 0 ? "bg-emerald-50 text-[#007A3D]" : "bg-red-50 text-[#CE1126]"}`}>
        <span>{t("netProfit")}</span><Money testid="pl-net-profit" value={d.net_profit} />
      </div>
    </div>
  );
}

function BalanceSheet({ end }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/balance-sheet", { params: { end } }).then((r) => setD(r.data)); }, [end]);
  if (!d) return null;
  return (
    <div className="grid grid-cols-1 gap-8 p-6 lg:grid-cols-2" data-testid="balance-sheet-report">
      <div><Section title={t("assets")} rows={d.assets} total={d.total_assets} totalLabel={t("totalAssets")} /></div>
      <div>
        <Section title={t("liabilities")} rows={d.liabilities} />
        <Section title={t("equity")} rows={[...d.equity, { id: "ce", code: "", name: t("currentEarnings"), balance: d.current_earnings }]} />
        <table className="data-table compact"><tbody><tr className="border-t-2 border-slate-900 font-bold"><td>{t("totalLiabEquity")}</td><td className="text-end"><Money testid="bs-total-le" value={d.total_liabilities_equity} /></td></tr></tbody></table>
        <div data-testid="bs-balanced-indicator" className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${d.balanced ? "bg-emerald-50 text-[#007A3D]" : "bg-red-50 text-[#CE1126]"}`}>
          {d.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{t(d.balanced ? "balancedOk" : "notBalanced")}
        </div>
      </div>
    </div>
  );
}

function CorporateTax({ year }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [sbr, setSbr] = useState(false);
  useEffect(() => { api.get("/reports/corporate-tax", { params: { year } }).then((r) => setD(r.data)); }, [year]);
  if (!d) return null;
  const due = sbr && d.sbr_eligible ? 0 : d.tax_estimated;
  const Row = ({ label, value, testid, mono = true, bold = false }) => (
    <tr className={bold ? "font-bold" : ""}><td className="text-slate-600">{label}</td><td className="text-end">{mono ? <Money testid={testid} value={value} /> : <span data-testid={testid}>{value}</span>}</td></tr>
  );
  return (
    <div className="max-w-2xl p-6" data-testid="corporate-tax-report">
      <p className="mb-4 text-sm text-slate-500">{t("ctSubtitle")} <span className="font-mono font-semibold text-slate-800">{d.year}</span></p>
      <div className="mb-5" data-testid="ct-progress">
        <div className="mb-1 flex justify-between text-xs text-slate-500"><span>{t("ctProgress")}</span><span className="font-mono font-semibold text-slate-800" data-testid="ct-progress-pct">{d.progress}%</span></div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full transition-[width] duration-700 ${d.progress >= 100 ? "bg-[#CE1126]" : d.progress >= 80 ? "bg-[#D4AF37]" : "bg-[#007A3D]"}`} style={{ width: `${d.progress}%` }} />
        </div>
        <p className="mt-1 text-[11px] text-slate-400">{d.progress >= 100 ? t("ctProgressOver") : d.progress >= 80 ? t("ctProgressNear") : t("ctProgressOk")}</p>
      </div>
      <table className="data-table compact"><tbody>
        <Row label={t("ctRevenue")} value={d.revenue} testid="ct-revenue" />
        <Row label={t("ctProfit")} value={d.net_profit} testid="ct-profit" />
        <Row label={t("ctThreshold")} value={d.threshold} testid="ct-threshold" />
        <Row label={t("ctTaxable")} value={d.taxable_above_threshold} testid="ct-taxable" />
        <Row label={t("ctRate")} value="9 %" testid="ct-rate" mono={false} />
      </tbody></table>
      <label className={`mt-5 flex items-start gap-3 rounded-lg border p-3 text-sm ${d.sbr_eligible ? "border-[#D4AF37]/50 bg-[#FFFBEB]" : "border-slate-200 bg-slate-50 opacity-70"}`}>
        <input type="checkbox" data-testid="ct-sbr-toggle" className="mt-1 accent-[#007A3D]" disabled={!d.sbr_eligible} checked={sbr && d.sbr_eligible} onChange={(e) => setSbr(e.target.checked)} />
        <span><span className="font-semibold text-slate-900">{t("ctSbr")}</span><br /><span className="text-xs text-slate-600">{d.sbr_eligible ? t("ctSbrText") : t("ctSbrNotEligible")}</span></span>
      </label>
      <div className={`mt-5 flex items-center justify-between rounded-lg p-4 text-lg font-bold ${due > 0 ? "bg-red-50 text-[#CE1126]" : "bg-emerald-50 text-[#007A3D]"}`}>
        <span>{t("ctEstimated")}{sbr && d.sbr_eligible && <span className="ms-2 rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-800">{t("ctSbrApplied")}</span>}</span>
        <Money testid="ct-due" value={due} />
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-4 text-sm" data-testid="ct-provision-box">
        <div>
          <div className="font-semibold text-slate-900">{t("ctProvision")}</div>
          <div className="text-xs text-slate-500">{t("ctProvisionText")}</div>
          <div className="mt-1 text-xs text-slate-600">{t("ctProvisionBooked")}: <Money testid="ct-provision-booked" value={d.provision_booked} className="font-semibold" /> · {t("ctProvisionGap")}: <Money testid="ct-provision-gap" value={d.provision_gap} className="font-semibold" /></div>
        </div>
        <Button data-testid="ct-provision-btn" size="sm" variant="outline" disabled={d.net_profit <= d.threshold || Math.abs(d.provision_gap) < 0.01} onClick={async () => {
          try { const r = await api.post("/reports/corporate-tax/provision", null, { params: { year } }); setD(r.data); toast.success(r.data.booked ? t("ctProvisionDone") : t("ctProvisionNone")); } catch (e) { toast.error(errMsg(e)); }
        }}>{t("ctProvisionNow")}</Button>
      </div>
      <CtPayments data={d} onChange={setD} />
      <p className="mt-4 text-xs text-slate-500">{t("ctNote")}</p>
    </div>
  );
}

export default function Reports() {
  const { t } = useLang();
  const [range, setRange] = useState({ start: yearStart(), end: today() });
  const year = Number(range.end.slice(0, 4));
  return (
    <div data-testid="reports-page">
      <PageHeader title={t("reports")} subtitle={t("reportsSubtitle")} actions={<DateRange testid="reports-range" start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} />} />
      <Tabs defaultValue="pl">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="pl" data-testid="tab-profit-loss">{t("profitLoss")}</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-balance-sheet">{t("balanceSheet")}</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-trial-balance">{t("trialBalance")}</TabsTrigger>
          <TabsTrigger value="ct" data-testid="tab-corporate-tax">{t("corporateTax")}</TabsTrigger>
          <TabsTrigger value="fx" data-testid="tab-fx">{t("fxReport")}</TabsTrigger>
          <TabsTrigger value="cf" data-testid="tab-cashflow">{t("cashflow")}</TabsTrigger>
        </TabsList>
        <Panel className="overflow-x-auto">
          <TabsContent value="pl" className="m-0"><ProfitLoss range={range} /></TabsContent>
          <TabsContent value="bs" className="m-0"><div className="px-6 pt-5 text-sm text-slate-500">{t("asOf")} <span className="font-mono">{range.end}</span></div><BalanceSheet end={range.end} /></TabsContent>
          <TabsContent value="tb" className="m-0"><TrialBalance range={range} /></TabsContent>
          <TabsContent value="ct" className="m-0"><CorporateTax year={year} /></TabsContent>
          <TabsContent value="fx" className="m-0"><FxReport range={range} /></TabsContent>
          <TabsContent value="cf" className="m-0"><Cashflow /></TabsContent>
        </Panel>
      </Tabs>
    </div>
  );
}
